import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiUrl, assetUrl } from '@/config/api';
import { useAuth } from '@/context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import normalizeLanguageCode, { deckLanguageOptions } from '@/utils/language.js';
import { deckTextColorOptions, getDeckCardTextStyle } from '@/utils/deckTextColors.js';
import CardPreviewModal from '@/components/CardPreviewModal.jsx';
import DeckImageEditor from '@/components/DeckImageEditor.jsx';
import DeckUploadProgress from '@/components/DeckUploadProgress.jsx';

const emptyDeckForm = { name: '', backgroundUrl: '', language: 'ua', textColor: '' };

const normalizeDeck = (deck) => ({
  ...deck,
  id: Number(deck.id),
  name: deck.name ?? '',
  description: deck.description ?? deck.text ?? '',
  backgroundUrl: deck.backgroundUrl ?? deck.cardBackgroundUrl ?? '',
  isSystem: Boolean(deck.isSystem ?? deck.system ?? deck.isDefault ?? deck.createdInAdmin),
  language: normalizeLanguageCode(deck.language),
  textColor: deck.textColor || '',
});

const normalizeCards = (cards) =>
  cards.map((card) => ({
    ...card,
    id: Number(card.id),
    cellNumber: card.cellNumber,
    title: card.title ?? '',
    text: card.text ?? '',
  }));

const DeckDetailsPage = () => {
  const { token, user } = useAuth();
  const { deckId } = useParams();
  const location = useLocation();
  const { t } = useTranslation('dashboard');
  const { navigate, getLocalizedPath } = useLanguageNavigation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deck, setDeck] = useState(null);
  const [deckForm, setDeckForm] = useState(emptyDeckForm);
  const [deckSaving, setDeckSaving] = useState(false);
  const [deckSuccess, setDeckSuccess] = useState('');
  const [deckFormError, setDeckFormError] = useState('');

  const [cards, setCards] = useState([]);
  const [cardsBaseline, setCardsBaseline] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState('');
  const [cardSavingMap, setCardSavingMap] = useState({});
  const [cardMessageMap, setCardMessageMap] = useState({});
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadBatch, setUploadBatch] = useState(null);
  const [uploadingCells, setUploadingCells] = useState({});
  const [imageUploadError, setImageUploadError] = useState('');

  const [cardBacks, setCardBacks] = useState([]);
  const [cardBacksError, setCardBacksError] = useState('');
  const [cardBacksLoading, setCardBacksLoading] = useState(false);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [backPickerOpen, setBackPickerOpen] = useState(false);
  const [backUploadError, setBackUploadError] = useState('');
  const [backUploading, setBackUploading] = useState(false);
  const [selectedBackUrl, setSelectedBackUrl] = useState('');
  const [backDeleteConfirm, setBackDeleteConfirm] = useState(null);
  const [backDeleteError, setBackDeleteError] = useState('');
  const [backDeleting, setBackDeleting] = useState(false);

  const backFileInputRef = useRef(null);

  const baselineMap = useMemo(() => {
    const map = {};
    cardsBaseline.forEach((card) => {
      map[card.id] = card;
    });
    return map;
  }, [cardsBaseline]);

  const cardBackOptions = useMemo(() => cardBacks, [cardBacks]);
  const cardBackUrl = useMemo(
    () => deck?.cardBack?.imageUrl || deckForm.backgroundUrl || '',
    [deck?.cardBack?.imageUrl, deckForm.backgroundUrl],
  );
  const resolvedCardBackUrl = useMemo(() => assetUrl(cardBackUrl), [cardBackUrl]);

  const firstCard = useMemo(() => {
    if (!cards || cards.length === 0) {
      return {
        cellNumber: 1,
        title: t('deckDetails.preview.emptyTitle'),
        text: t('deckDetails.preview.emptyText'),
      };
    }
    const fromFirstCell = cards.find((card) => card.cellNumber === 1);
    if (fromFirstCell) {
      return fromFirstCell;
    }
    return cards[0];
  }, [cards, t]);

  const readOnly = deck?.isSystem;
  const numericDeckId = deckId ? Number(deckId) : null;

  const fetchDeckInfo = useCallback(async () => {
    if (!token || !numericDeckId) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(apiUrl('/api/decks/my'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('deckDetails.messages.deckLoadError'));
      }
      const data = await response.json();
      const list = Array.isArray(data.decks) ? data.decks : [];
      const normalizedDecks = list.map(normalizeDeck);
      const found = normalizedDecks.find((item) => item.id === numericDeckId);
      if (!found) {
        throw new Error(t('deckDetails.messages.deckNotFound'));
      }
      setDeck(found);
      setDeckForm({
        name: found.name,
        backgroundUrl: found.backgroundUrl || '',
        language: found.language,
        textColor: found.textColor || '',
      });
      setDeckSuccess('');
      setDeckFormError('');
    } catch (fetchError) {
      setError(fetchError.message);
      setDeck(null);
      setDeckForm(emptyDeckForm);
    } finally {
      setLoading(false);
    }
  }, [numericDeckId, t, token]);

  const fetchCards = useCallback(async () => {
    if (!numericDeckId || !token) {
      return;
    }
    setCardsLoading(true);
    setCardsError('');
    try {
      const response = await fetch(apiUrl(`/api/decks/${numericDeckId}/cards`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('deckDetails.messages.cardsLoadError'));
      }
      const data = await response.json();
      const list = Array.isArray(data.cards) ? data.cards : data;
      const normalized = normalizeCards(list ?? []);
      setCards(normalized);
      setCardsBaseline(normalized);
      setCardMessageMap({});
      setCardSavingMap({});
    } catch (cardsFetchError) {
      setCardsError(cardsFetchError.message);
      setCards([]);
      setCardsBaseline([]);
      setCardMessageMap({});
      setCardSavingMap({});
    } finally {
      setCardsLoading(false);
    }
  }, [numericDeckId, t, token]);

  const fetchCardBacks = useCallback(async () => {
    if (!token) {
      return;
    }
    setCardBacksLoading(true);
    setCardBacksError('');
    try {
      const response = await fetch(apiUrl('/api/card-backs'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('deckDetails.messages.cardBacksError'));
      }
      const data = await response.json();
      const backs = Array.isArray(data.cardBacks) ? data.cardBacks : [];
      setCardBacks(backs);
    } catch (backsError) {
      setCardBacksError(backsError.message);
      setCardBacks([]);
    } finally {
      setCardBacksLoading(false);
    }
  }, [t, token]);

  useEffect(() => {
    fetchDeckInfo();
  }, [fetchDeckInfo]);

  useEffect(() => {
    if (deck) {
      fetchCards();
    }
  }, [deck, fetchCards]);

  useEffect(() => {
    fetchCardBacks();
  }, [fetchCardBacks]);

  useEffect(() => {
    setSelectedBackUrl(cardBackUrl);
    setBackUploadError('');
  }, [cardBackUrl]);

  const handleDeckFormChange = (field, value) => {
    if (readOnly) {
      return;
    }
    const nextValue = field === 'language' ? normalizeLanguageCode(value) : value;
    setDeckForm((prev) => ({ ...prev, [field]: nextValue }));
    setDeckSuccess('');
    setDeckFormError('');
  };

  const openPreviewModal = () => {
    if (!cardBackUrl) {
      return;
    }
    setPreviewModalOpen(true);
  };

  const handleBackSelection = (url) => {
    setSelectedBackUrl(url);
    setBackUploadError('');
  };

  const handleBackSave = () => {
    if (readOnly) {
      return;
    }
    handleDeckFormChange('backgroundUrl', selectedBackUrl || '');
    setBackPickerOpen(false);
  };

  const canDeleteCardBack = (back) =>
    Number.isInteger(back?.id) && !back.isSystem && back.ownerId && back.ownerId === user?.id;

  const requestDeleteCardBack = (back) => {
    setBackDeleteConfirm(back);
    setBackDeleteError('');
  };

  const closeDeleteConfirm = () => {
    setBackDeleteConfirm(null);
    setBackDeleteError('');
  };

  const handleBackDelete = async () => {
    if (!backDeleteConfirm?.id || !token || readOnly) {
      return;
    }
    setBackDeleting(true);
    setBackDeleteError('');
    try {
      const response = await fetch(apiUrl(`/api/card-backs/${backDeleteConfirm.id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.error || t('deckDetails.messages.cardBackDeleteError');
        throw new Error(message === 'INTERNAL_ERROR' ? t('deckDetails.messages.cardBackDeleteError') : message);
      }
      setCardBacks((prev) => prev.filter((back) => back.id !== backDeleteConfirm.id));
      if (selectedBackUrl === backDeleteConfirm.imageUrl) {
        handleDeckFormChange('backgroundUrl', '');
        setSelectedBackUrl('');
      }
      closeDeleteConfirm();
    } catch (deleteError) {
      setBackDeleteError(deleteError.message);
    } finally {
      setBackDeleting(false);
    }
  };

  const handleBackFileChange = async (event) => {
    if (readOnly) {
      return;
    }

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!token) {
      setBackUploadError(t('deckDetails.messages.authRequired'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setBackUploadError(t('deckDetails.messages.fileSize'));
      event.target.value = '';
      return;
    }

    setBackUploading(true);
    setBackUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(apiUrl('/api/upload/card-back'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || t('deckDetails.messages.fileUploadError'));
      }
      if (data.url) {
        const newBack = data.cardBack || {
          id: `custom-${Date.now()}`,
          name: file.name?.split('.')?.[0] || t('deckDetails.cardBack.custom'),
          imageUrl: data.url,
          thumbnailUrl: data.thumbnailUrl || data.url,
          isSystem: false,
          ownerId: user?.id ?? null,
        };
        setCardBacks((prev) => {
          const filtered = prev.filter((back) => back.imageUrl !== newBack.imageUrl);
          const displayName =
            newBack.name || t('deckDetails.cardBack.customWithIndex', { index: filtered.length + 1 });
          return [{ ...newBack, name: displayName }, ...filtered];
        });
        handleDeckFormChange('backgroundUrl', newBack.imageUrl);
        setSelectedBackUrl(newBack.imageUrl);
      }
    } catch (uploadError) {
      setBackUploadError(uploadError.message);
    } finally {
      setBackUploading(false);
      if (backFileInputRef.current) {
        backFileInputRef.current.value = '';
      }
    }
  };

  const handleDeckSave = async (event) => {
    event.preventDefault();
    if (!numericDeckId) {
      return;
    }
    if (readOnly) {
      setDeckFormError(t('deckDetails.messages.deckReadOnly'));
      return;
    }
    if (!deckForm.name.trim()) {
      setDeckFormError(t('deckDetails.messages.nameRequired'));
      return;
    }
    try {
      setDeckSaving(true);
      const response = await fetch(apiUrl(`/api/decks/${numericDeckId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: deckForm.name.trim(),
          cardBackgroundUrl: deckForm.backgroundUrl?.trim() || null,
          language: deckForm.language,
          textColor: deckForm.textColor || null,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('deckDetails.messages.deckSaveError'));
      }
      const data = await response.json().catch(() => ({}));
      const updated = data.deck ? normalizeDeck(data.deck) : null;
      if (updated) {
        setDeck(updated);
      }
      setDeckSuccess(t('deckDetails.messages.saveSuccess'));
    } catch (deckError) {
      setDeckFormError(deckError.message);
    } finally {
      setDeckSaving(false);
    }
  };

  const handleCardInputChange = (cardId, field, value) => {
    if (readOnly) {
      return;
    }
    setCards((prev) => prev.map((card) => (card.id === cardId ? { ...card, [field]: value } : card)));
  };

  const isCardDirty = (card) => {
    const baseline = baselineMap[card.id];
    if (!baseline) {
      return true;
    }
    return baseline.title !== card.title || baseline.text !== card.text;
  };

  const handleCardImagesSelected = async (entries) => {
    if (readOnly || !token || !numericDeckId || Object.values(uploadingCells).some(Boolean)) return;
    setImageUploadError('');
    let completed = 0;
    let failures = 0;
    let cursor = 0;
    setUploadProgress(t('imageDeck.progress', { count: 0, total: entries.length }));
    setUploadBatch(entries.length > 1 ? { done: 0, total: entries.length, failed: 0 } : null);
    const worker = async () => {
      while (cursor < entries.length) {
        const [cellNumber, file] = entries[cursor++];
        setUploadingCells((prev) => ({ ...prev, [cellNumber]: true }));
        try {
          const body = new FormData();
          body.append('file', file);
          const response = await fetch(apiUrl(`/api/decks/${numericDeckId}/cards/${cellNumber}/image`), {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || t('imageDeck.uploadFailure'));
          const update = (prev) => prev.map((card) =>
            card.cellNumber === cellNumber ? { ...card, imageUrl: data.imageUrl } : card);
          setCards(update);
          setCardsBaseline(update);
        } catch (uploadError) {
          failures += 1;
          setImageUploadError(uploadError.message);
        } finally {
          completed += 1;
          setUploadProgress(t('imageDeck.progress', { count: completed, total: entries.length }));
          setUploadBatch(entries.length > 1 ? { done: completed, total: entries.length, failed: failures } : null);
          setUploadingCells((prev) => ({ ...prev, [cellNumber]: false }));
        }
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(3, entries.length) }, worker));
    } finally {
      setUploadBatch(null);
    }
    if (failures) setImageUploadError(t('imageDeck.uploadFailure'));
  };

  const handleCardSave = async (card) => {
    if (readOnly || !isCardDirty(card) || !token) {
      return;
    }
    setCardMessageMap((prev) => ({ ...prev, [card.id]: { success: '', error: '' } }));
    setCardSavingMap((prev) => ({ ...prev, [card.id]: true }));
    try {
      const response = await fetch(apiUrl(`/api/cards/${card.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: card.title,
          text: card.text,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('deckDetails.cards.errors.save'));
      }
      setCardsBaseline((prev) =>
        prev.map((baselineCard) => (baselineCard.id === card.id ? { ...card } : baselineCard)),
      );
      setCardMessageMap((prev) => ({
        ...prev,
        [card.id]: { success: t('deckDetails.cards.success'), error: '' },
      }));
    } catch (cardError) {
      setCardMessageMap((prev) => ({
        ...prev,
        [card.id]: { success: '', error: cardError.message },
      }));
    } finally {
      setCardSavingMap((prev) => ({ ...prev, [card.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="page-card">
        <p className="page-description">{t('deckDetails.states.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-card">
        <h1>{t('deckDetails.title')}</h1>
        <p className="form-error">{error}</p>
        <button
          type="button"
          className="ghost-button"
          onClick={() => navigate('/dashboard/my-cards')}
        >
          {t('deckDetails.buttons.backToList')}
        </button>
      </div>
    );
  }

  const cardsCount = cards?.length || 72;

  return (
    <div className="page-card">
      <div className="page-card-header deck-header">
        <div>
          <p className="page-caption">{t('deckDetails.caption')}</p>
          <h1>{t('deckDetails.titleNamed', { name: deck?.name || t('deckDetails.untitled') })}</h1>
          <p className="page-description">
            {readOnly ? t('deckDetails.description.system') : t('deckDetails.description.editable')}
          </p>
        </div>
        <div className="deck-header-actions">
          <button
            type="button"
            className="ghost-button"
            onClick={() => navigate('/dashboard/my-cards', { state: { selectDeckId: deck?.id } })}
          >
            {t('deckDetails.buttons.backToList')}
          </button>
          {!readOnly && (
            <button
              type="submit"
              className="primary-button"
              form="deck-form"
              disabled={deckSaving}
            >
              {deckSaving ? t('deckDetails.buttons.savingChanges') : t('deckDetails.buttons.saveChanges')}
            </button>
          )}
        </div>
      </div>

      {deck && (
        <div className="deck-editor-grid">
          <form id="deck-form" className="deck-form-card" onSubmit={handleDeckSave}>
            <h2>{t('deckDetails.parametersTitle')}</h2>
            {readOnly && <p className="form-hint">{t('deckDetails.readOnlyHint')}</p>}
            <label className="form-field">
              <span>{t('deckDetails.fields.name.label')}</span>
              <input
                type="text"
                value={deckForm.name}
                onChange={(event) => handleDeckFormChange('name', event.target.value)}
                placeholder={t('deckDetails.fields.name.placeholder')}
                disabled={readOnly}
              />
            </label>
            <label className="form-field">
              <span>{t('deckDetails.fields.language.label')}</span>
              {readOnly ? (
                <div className="deck-language-label" aria-live="polite">
                  {t(`deckDetails.languages.${normalizeLanguageCode(deckForm.language)}`)}
                </div>
              ) : (
                <select
                  value={deckForm.language}
                  onChange={(event) => handleDeckFormChange('language', event.target.value)}
                  disabled={readOnly}
                >
                  {deckLanguageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(`deckDetails.languages.${option.value}`)}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <div className="form-field">
              <span className="form-hint">{t('imageDeck.modeLabel')}: {t(deck?.renderMode === 'IMAGE' ? 'imageDeck.imageMode' : 'imageDeck.textMode')}</span>
            </div>
            {deck?.renderMode !== 'IMAGE' && (<>
            <div className="form-field card-back-preview-field">
              <div className="card-back-preview-header">
                <span>{t('deckDetails.fields.cardBack.label')}</span>
                <span className="form-hint">{t('deckDetails.fields.cardBack.hint')}</span>
              </div>
              <div className={`card-back-preview${cardBackUrl ? '' : ' empty'}${readOnly ? ' read-only' : ''}`}>
                <div
                  className="card-back-preview__image"
                  style={cardBackUrl ? { backgroundImage: `url(${resolvedCardBackUrl})` } : {}}
                >
                  {!cardBackUrl && (
                    <span className="card-back-preview__placeholder">
                      {t('deckDetails.fields.cardBack.placeholder')}
                    </span>
                  )}
                  <div className="card-back-preview__actions">
                    <button
                      type="button"
                      className="icon-button"
                      onClick={openPreviewModal}
                      disabled={!cardBackUrl}
                      title={t('deckDetails.fields.cardBack.preview')}
                    >
                      👁
                    </button>
                    {!readOnly && (
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => setBackPickerOpen(true)}
                        title={t('deckDetails.fields.cardBack.edit')}
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {cardBacksError && <p className="form-error">{cardBacksError}</p>}
            </div>
            <label className="form-field">
              <span>{t('deckDetails.fields.textColor.label')}</span>
              <select
                value={deckForm.textColor}
                onChange={(event) => handleDeckFormChange('textColor', event.target.value)}
                disabled={readOnly}
              >
                {deckTextColorOptions.map((option) => (
                  <option key={option.value || 'default'} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>
            </>)}
            {deckFormError && <p className="form-error">{deckFormError}</p>}
            {deckSuccess && <p className="form-success">{deckSuccess}</p>}
            {!readOnly && (
              <button type="submit" className="primary-button" disabled={deckSaving}>
                {deckSaving ? t('deckDetails.buttons.savingDeck') : t('deckDetails.buttons.saveDeck')}
              </button>
            )}
          </form>

          <div className="cards-section">
            <div className="cards-section-header">
              <h2>{t('deckDetails.cards.title', { count: cardsCount })}</h2>
              <p>
                {readOnly
                  ? t('deckDetails.cards.description.system')
                  : t('deckDetails.cards.description.editable')}
              </p>
            </div>
            {cardsError && <p className="form-error">{cardsError}</p>}
            {cardsLoading ? (
              <p className="page-description">{t('deckDetails.states.cardsLoading')}</p>
            ) : deck?.renderMode === 'IMAGE' ? (
              <>
                {location.state?.imageDeckUploadFailed && <p className="form-error">{t('imageDeck.uploadFailure')}</p>}
                {imageUploadError && <p className="form-error">{imageUploadError}</p>}
                <DeckImageEditor cards={cards} selectedFiles={{}} onFilesSelected={handleCardImagesSelected}
                  busy={readOnly} uploadingCells={uploadingCells} progress={uploadProgress} />
              </>
            ) : (
              <div className="cards-table-wrapper">
                <table className="cards-table">
                  <thead>
                    <tr>
                      <th>{t('deckDetails.cards.headers.cell')}</th>
                      <th>{t('deckDetails.cards.headers.title')}</th>
                      <th>{t('deckDetails.cards.headers.text')}</th>
                      <th aria-label={t('deckDetails.cards.headers.actions')} />
                    </tr>
                  </thead>
                  <tbody>
                    {cards.map((card) => {
                      const dirty = isCardDirty(card);
                      const status = cardMessageMap[card.id] || { success: '', error: '' };
                      return (
                        <tr key={card.id}>
                          <td className="cell-number">{card.cellNumber}</td>
                          <td>
                            <input
                              type="text"
                              className="card-input"
                              value={card.title}
                              onChange={(event) =>
                                handleCardInputChange(card.id, 'title', event.target.value)
                              }
                              placeholder={t('deckDetails.cards.placeholders.title')}
                              disabled={readOnly}
                            />
                          </td>
                          <td>
                            <textarea
                              className="card-textarea"
                              value={card.text}
                              onChange={(event) =>
                                handleCardInputChange(card.id, 'text', event.target.value)
                              }
                              placeholder={t('deckDetails.cards.placeholders.text')}
                              rows={3}
                              disabled={readOnly}
                            />
                          </td>
                          <td>
                            <div className="card-actions">
                              {!readOnly && (
                                <button
                                  type="button"
                                  className="ghost-button"
                                  disabled={!dirty || cardSavingMap[card.id]}
                                  onClick={() => handleCardSave(card)}
                                >
                                  {cardSavingMap[card.id]
                                    ? t('deckDetails.cards.saving')
                                    : t('deckDetails.cards.save')}
                                </button>
                              )}
                              {status.success && <span className="card-success">{status.success}</span>}
                              {status.error && <span className="card-error">{status.error}</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <CardPreviewModal
        open={previewModalOpen}
        card={{
          cellNumber: firstCard.cellNumber,
          title: firstCard.title || t('deckDetails.preview.emptyTitle'),
          text: firstCard.text || t('deckDetails.preview.emptyText'),
          imageUrl: firstCard.imageUrl || null,
        }}
        onClose={() => setPreviewModalOpen(false)}
        backgroundUrl={resolvedCardBackUrl}
        textStyle={getDeckCardTextStyle(deckForm.textColor)}
        emptyMessage={t('deckDetails.preview.emptyText')}
        closeLabel={t('deckDetails.preview.close')}
      />

      {backPickerOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal card-back-modal">
            <div className="modal-header">
              <h3>{t('deckDetails.backPicker.title')}</h3>
              <button type="button" className="ghost-button" onClick={() => setBackPickerOpen(false)}>
                {t('deckDetails.backPicker.close')}
              </button>
            </div>
            {cardBacksLoading ? <p className="page-description">{t('deckDetails.backPicker.loading')}</p> : null}
            <div className="card-back-grid modern">
              {cardBackOptions.map((back) => (
                <div
                  key={back.id || back.imageUrl}
                  className={`card-back-card${selectedBackUrl === back.imageUrl ? ' active' : ''}`}
                  onClick={() => handleBackSelection(back.imageUrl)}
                  role="button"
                  tabIndex={0}
                >
                  {!readOnly && canDeleteCardBack(back) && (
                    <span className="card-back-card__actions">
                      <button
                        type="button"
                        className="card-back-card__delete"
                        onClick={(event) => {
                          event.stopPropagation();
                          requestDeleteCardBack(back);
                        }}
                        title={t('deckDetails.backPicker.deleteAction')}
                        aria-label={t('deckDetails.backPicker.deleteAction')}
                      >
                        🗑️
                      </button>
                    </span>
                  )}
                  <div
                    className="card-back-card__image"
                    style={{ backgroundImage: `url(${assetUrl(back.thumbnailUrl || back.imageUrl)})` }}
                  />
                  <span className="card-back-card__title">{back.name}</span>
                </div>
              ))}
              {deckForm.backgroundUrl &&
                !cardBackOptions.some((back) => back.imageUrl === deckForm.backgroundUrl) && (
                  <div
                    className={`card-back-card${selectedBackUrl === deckForm.backgroundUrl ? ' active' : ''}`}
                    onClick={() => handleBackSelection(deckForm.backgroundUrl)}
                    role="button"
                    tabIndex={0}
                  >
                    <div
                      className="card-back-card__image"
                      style={{ backgroundImage: `url(${assetUrl(deckForm.backgroundUrl)})` }}
                    />
                    <span className="card-back-card__title">{t('deckDetails.backPicker.current')}</span>
                  </div>
                )}
              {!readOnly && (
                <label className={`card-back-card add${backUploading ? ' loading' : ''}`}>
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    onChange={handleBackFileChange}
                    ref={backFileInputRef}
                    disabled={backUploading}
                  />
                  <span className="card-back-card__plus">+</span>
                  <span className="card-back-card__title">
                    {backUploading ? t('deckDetails.backPicker.adding') : t('deckDetails.backPicker.add')}
                  </span>
                  <span className="card-back-card__hint">{t('deckDetails.backPicker.hint')}</span>
                </label>
              )}
            </div>
            {backUploadError && <p className="form-error">{backUploadError}</p>}
            <div className="modal-actions">
              <button type="button" className="ghost-button" onClick={() => setBackPickerOpen(false)}>
                {t('deckDetails.backPicker.cancel')}
              </button>
              <button type="button" className="primary-button" onClick={handleBackSave} disabled={readOnly}>
                {t('deckDetails.backPicker.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <DeckUploadProgress value={uploadBatch} label={t('imageDeck.uploadingImages')} />
      {backDeleteConfirm && (
        <div className="modal-backdrop" role="alertdialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>{t('deckDetails.backPicker.deleteConfirm')}</h3>
            </div>
            {backDeleteError && <p className="form-error">{backDeleteError}</p>}
            <div className="modal-actions">
              <button type="button" className="ghost-button" onClick={closeDeleteConfirm} disabled={backDeleting}>
                {t('deckDetails.backPicker.deleteCancel')}
              </button>
              <button type="button" className="primary-button" onClick={handleBackDelete} disabled={backDeleting}>
                {backDeleting ? t('deckDetails.backPicker.deleting') : t('deckDetails.backPicker.deleteConfirmButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeckDetailsPage;
